// S-7 `/verify-code` — ввод 6-значного OTP-кода.
// Source: docs/specs/auth-otp-verification/spec.md §3 + §4.2.
//
// OTP-флоу: пользователь НЕ покидает приложение. 6 ячеек на базе input-otp,
// autoComplete="one-time-code" + inputMode="numeric" (iOS+Mail подхватывает код
// из QuickType-бара, §7). По вводу 6 цифр (onComplete) — авто-submit verify-code:
//   200 → setToken → invalidate profileMe → / (автологин, D2/D7)
//   422 → toast «неверный код» + очистка ячеек
//   429 → toast «слишком много попыток»
//
// Resend честно работает в ОБОИХ mode (cooldown 60с): signup без challenge,
// signin с verificationChallenge (D5). Пароль в памяти не хранится.
//
// Дизайн: палитра лендинга (primary-зелёный, gradient-hero, blur-blob). Ячейки —
// отдельные, с зелёной рамкой и зелёным active-ring; resend — зелёная ссылка-кнопка.

import { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, MailCheck } from 'lucide-react';
import { useResendCodeMutation, useVerifyCodeMutation } from '@/api/hooks/auth';
import { queryKeys } from '@/api/queryKeys';
import { isApiError } from '@/api/error';
import { useAwaitVerifyContext } from '@/context/AwaitVerifyContext';
import { setToken } from '@/lib/auth-storage';
import { useToast } from '@/hooks/use-toast';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

const RESEND_COOLDOWN_S = 60;
const CODE_LENGTH = 6;

// Зелёная рамка + зелёный active-ring (палитра лендинга), отдельные ячейки.
const SLOT_CLASS =
  'h-14 w-11 rounded-surface border-primary/30 bg-background text-2xl font-bold text-foreground shadow-sm ' +
  'ring-primary ring-offset-2 transition-all first:rounded-surface last:rounded-surface ' +
  'data-[active=true]:border-primary';

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

export function VerifyCode() {
  const [, setLocation] = useLocation();
  const awaitVerify = useAwaitVerifyContext();
  const verifyMutation = useVerifyCodeMutation();
  const resendMutation = useResendCodeMutation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!awaitVerify.state) {
    return <Redirect to="/signin" />;
  }

  const { state } = awaitVerify;

  const handleComplete = async (value: string) => {
    if (verifyMutation.isPending) return;
    try {
      const response = await verifyMutation.mutateAsync({
        email: state.email,
        code: value,
        verification_challenge: state.verificationChallenge,
      });
      await setToken(response.token);
      awaitVerify.clear();
      await queryClient.invalidateQueries({ queryKey: queryKeys.profileMe });
      setLocation('/');
    } catch (err) {
      setCode('');
      if (!isApiError(err)) {
        toast({
          title: 'Ошибка',
          description: 'Неизвестная ошибка, попробуйте ещё раз',
          variant: 'destructive',
        });
        return;
      }
      const status = err.response?.status;
      if (status === 422) {
        toast({
          title: 'Неверный или истёкший код',
          description: 'Проверьте код из письма или запросите новый',
          variant: 'destructive',
        });
      } else if (status === 429) {
        toast({
          title: 'Слишком много попыток',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Ошибка сервера',
          description: 'Попробуйте ещё раз',
          variant: 'destructive',
        });
      }
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resendMutation.isPending) return;
    try {
      await resendMutation.mutateAsync({
        email: state.email,
        verification_challenge: state.verificationChallenge,
      });
      // resend идемпотентен (generic 200) — UX-фидбек одинаковый независимо от
      // того, отправил ли сервер реально (anti-enumeration).
      toast({ title: 'Если код ещё актуален, мы отправили его повторно' });
      setCooldown(RESEND_COOLDOWN_S);
      setCode('');
    } catch (err) {
      if (isApiError(err) && err.response?.status === 429) {
        toast({
          title: 'Слишком много запросов',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
        });
        setCooldown(RESEND_COOLDOWN_S);
      } else {
        toast({
          title: 'Не удалось отправить код',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
        });
      }
    }
  };

  const resendDisabled = cooldown > 0 || resendMutation.isPending;

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/signin')} title="Подтверждение" />

      {/* Gradient-hero как на лендинге (from-primary/5 → fade) + blur-blob. */}
      <div className="relative flex flex-1 flex-col overflow-hidden bg-gradient-to-b from-primary/5 to-transparent px-6 pb-6 pt-4">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

        <div className="relative flex flex-1 flex-col">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-surface bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/25">
              <MailCheck className="h-8 w-8" strokeWidth={1.75} />
            </div>
            <h1 className="mb-1.5 text-hl-title font-bold leading-tight tracking-tight">
              Введите код из письма
            </h1>
            <p className="text-sm leading-snug text-muted-foreground">
              Мы отправили 6-значный код на
              <br />
              <span className="font-semibold text-foreground">{maskEmail(state.email)}</span>
            </p>
          </div>

          <div className="mb-3 flex justify-center">
            <InputOTP
              maxLength={CODE_LENGTH}
              value={code}
              onChange={setCode}
              onComplete={(v) => void handleComplete(v)}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d*"
              disabled={verifyMutation.isPending}
              containerClassName="justify-center gap-2 sm:gap-2.5"
            >
              {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <InputOTPGroup key={i}>
                  <InputOTPSlot index={i} className={SLOT_CLASS} />
                </InputOTPGroup>
              ))}
            </InputOTP>
          </div>

          <div className="mb-7 flex h-5 items-center justify-center">
            {verifyMutation.isPending && (
              <span className="flex items-center gap-1.5 text-hl-secondary font-medium text-primary-text">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Проверяем код…
              </span>
            )}
          </div>

          {/* Resend — зелёная ссылка-кнопка, не «дизейбл-бокс». */}
          <div className="text-center text-sm text-muted-foreground">
            Не пришёл код?{' '}
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={resendDisabled}
              className="font-semibold text-primary-text underline-offset-2 transition-colors hover:text-primary/80 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
            >
              {cooldown > 0 ? `Отправить ещё раз (${cooldown}с)` : 'Отправить ещё раз'}
            </button>
          </div>

          <div className="mt-auto pt-8">
            <div className="rounded-surface border border-primary/20 bg-primary/5 p-3.5 text-hl-small leading-snug">
              <div className="mb-0.5 font-semibold text-foreground">Не нашли письмо?</div>
              <span className="text-muted-foreground">
                Проверьте папку «Спам». Код приходит с адреса{' '}
                <span className="font-mono text-foreground">noreply@healthlab.kz</span> и действует
                10 минут.
              </span>
            </div>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
